import React, { useMemo } from 'react';
import Highlighter from 'react-highlight-words';

export interface SearchHighlighterProps {
  searchValue: string;
  textToHighlight: string;
  isNumber?: boolean;
  searchContains?: boolean;
  searchCaseSensitive?: boolean;
}

const SearchHighlighter = ({
  searchValue,
  textToHighlight,
  isNumber,
  searchContains = false,
  searchCaseSensitive = false,
}: SearchHighlighterProps): React.JSX.Element => {
  const findChunks = useMemo(() => {
    if (searchContains && !isNumber) return undefined;

    return ({ textToHighlight: text }: { textToHighlight: string }) => {
      if (!searchValue) return [];

      if (isNumber) {
        const digitsOnly = text.replace(/\D/g, '');
        const targetStart = searchContains
          ? digitsOnly.indexOf(searchValue)
          : digitsOnly.startsWith(searchValue)
            ? 0
            : -1;
        if (targetStart === -1) return [];
        const targetEnd = targetStart + searchValue.length;
        let digitCount = 0;
        let startPos = -1;
        let endPos = -1;
        for (let i = 0; i < text.length; i++) {
          if (/\d/.test(text[i])) {
            if (digitCount === targetStart) startPos = i;
            digitCount++;
            if (digitCount === targetEnd) {
              endPos = i + 1;
              break;
            }
          }
        }
        return startPos !== -1 && endPos !== -1
          ? [{ start: startPos, end: endPos }]
          : [];
      }

      const trimStart = text.length - text.trimStart().length;
      const trimmedText = text.trimStart();
      const haystack = searchCaseSensitive
        ? trimmedText
        : trimmedText.toLowerCase();
      const needle = searchCaseSensitive
        ? searchValue
        : searchValue.toLowerCase();
      if (haystack.startsWith(needle)) {
        return [{ start: trimStart, end: trimStart + searchValue.length }];
      }
      return [];
    };
  }, [searchContains, isNumber, searchValue, searchCaseSensitive]);

  return (
    <Highlighter
      autoEscape
      caseSensitive={searchCaseSensitive}
      findChunks={findChunks}
      searchWords={[searchValue]}
      textToHighlight={textToHighlight}
    />
  );
};

export default SearchHighlighter;
